import { Box, FormLabel, HStack, Radio, Text } from "@chakra-ui/react";
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import AsyncSelect from "react-select/async";
import { useUserInfo } from "../../../utils/hooks/useUserInfo";
import DatePicker from "../../common/DateRangePicker";
import {
  SearchAndFilterForm,
  loadBatch,
  loadDispositionSettings,
  loadLeadSource,
  loadLines,
  loadRoles,
  loadTags,
  loadUsers,
} from "./utils";
import { loadDealershipUsers } from "../../Leads/utils";
import { roleChecker } from "../../../utils";
import DebouncedAsyncSelect from "../../common/DebouncedAsyncSelect";
import { IncludeExcludeRadio } from "../../common/IncludeExcludeRadio";
type ContactPropertiesProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  dealership_id?: string;
  prefix?: string;
};

function ContactProperties({
  control,
  setValue,
  dealership_id,
  prefix = "",
}: ContactPropertiesProps) {
  const user = useUserInfo("user");
  const { dealership } = user || {};
  const role = useUserInfo("role");

  const id = dealership?.id ?? dealership_id;

  const roles: any[] = useWatch({
    control,
    name: `${prefix}filter_and_sort.contact_attributes.roles`,
  });

  const mappedRoles = roles?.reduce(
    (acc, role) => (acc += `${(role as any).label},`),
    ""
  );

  const contactAttributes = useWatch({
    control,
    name: `${prefix}filter_and_sort.contact_attributes`,
  });

  return (
    <>
      <Box background="var(--secondary-50)" padding="1.25rem">
        <Text textStyle="h4" color="var(--grey-900)" fontWeight="600">
          Contact Attributes
        </Text>
      </Box>
      <Box padding="1.25rem">
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Disposition Status
                  </FormLabel>
                  <IncludeExcludeRadio
                    value={contactAttributes?.disposition_status_exclusive}
                    onChange={() => 
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.disposition_status_exclusive`, 
                        !contactAttributes?.disposition_status_exclusive
                      )
                    }
                    size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  loadOptions={(searchStr) =>
                    loadDispositionSettings(searchStr, id)
                  }
                  defaultOptions
                  closeMenuOnSelect={false}
                  value={value}
                  onChange={(value: any) => {
                    if (value?.some((dis: any) => dis.value === "all")) {
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.disposition_status`,
                        [{ value: "all", label: "Select All" }]
                      );
                      return;
                    }
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.disposition_status`,
                      value
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Disposition Status"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.disposition_status`}
          control={control}
          rules={{
            required: true,
          }}
        />
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Tags
                  </FormLabel>
                  <IncludeExcludeRadio
                    value={contactAttributes?.tags_exclusive}
                    onChange={() => 
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.tags_exclusive`, 
                        !contactAttributes?.tags_exclusive
                      )
                    }
                    size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  loadOptions={(searchStr) => loadTags(searchStr, id)}
                  defaultOptions
                  value={value}
                  onChange={(value: any) => {
                    if (value?.some((dis: any) => dis.value === "all")) {
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.tags`,
                        [{ value: "all", label: "Select All" }]
                      );
                      return;
                    }
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.tags`,
                      value
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Multiple Tags"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.tags`}
          control={control}
          rules={{
            required: true,
          }}
        />

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Lead/Contact Source 
                  </FormLabel>
                  <IncludeExcludeRadio
                      value={contactAttributes?.lead_source_exclusive}
                      onChange={() => 
                        setValue(
                          `${prefix}filter_and_sort.contact_attributes.lead_source_exclusive`, 
                          !contactAttributes?.lead_source_exclusive
                        )
                      }
                      size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  loadOptions={(searchStr) => loadLeadSource(searchStr, id)}
                  defaultOptions
                  value={value}
                  onChange={(value: any) => {
                    if (value?.some((dis: any) => dis.value === "all")) {
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.lead_source`,
                        [{ value: "all", label: "Select All" }]
                      );
                      return;
                    }
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.lead_source`,
                      value
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Lead/Contact Source"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.lead_source`}
          control={control}
          rules={{
            required: true,
          }}
        />

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Lines 
                  </FormLabel>
                  <IncludeExcludeRadio
                    value={contactAttributes?.lines_exclusive}
                    onChange={() => 
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.lines_exclusive`, 
                        !contactAttributes?.lines_exclusive
                      )
                    }
                    size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  loadOptions={(searchStr) => loadLines(searchStr, id)}
                  defaultOptions
                  value={value}
                  onChange={(value: any) => {
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.lines`,
                      value
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Lines"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.lines`}
          control={control}
          rules={{
            required: true,
          }}
        />

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Import 
                  </FormLabel>
                  <IncludeExcludeRadio
                    value={contactAttributes?.batch_id_exclusive}
                    onChange={() => 
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.batch_id_exclusive`, 
                        !contactAttributes?.batch_id_exclusive
                      )
                    }
                    size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  loadOptions={(searchStr) => loadBatch(searchStr, id)}
                  defaultOptions
                  value={value}
                  onChange={(value: any) => {
                    if (value?.some((dis: any) => dis.value === "all")) {
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.batch_id`,
                        [{ value: "all", label: "Select All" }]
                      );
                      return;
                    }
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.batch_id`,
                      value
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Batches"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.batch_id`}
          control={control}
          rules={{
            required: true,
          }}
        />

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Select Roles
                  </FormLabel>
                  <IncludeExcludeRadio
                    value={contactAttributes?.roles_exclusive}
                    onChange={() => 
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.roles_exclusive`, 
                        !contactAttributes?.roles_exclusive
                      )
                    }
                    size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  loadOptions={(searchStr) => loadRoles(searchStr, id)}
                  defaultOptions
                  value={value}
                  onChange={(value: any) => {
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.roles`,
                      value
                    );
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.users`,
                      []
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Roles"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.roles`}
          control={control}
          rules={{
            required: true,
          }}
        />

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <Box mb="1rem">
                <HStack justifyContent={"space-between"}>
                  <FormLabel
                    mb="0.31rem"
                    color="var(--grey-900)"
                    fontSize="0.875rem"
                    fontWeight="600"
                    lineHeight="1rem"
                  >
                    Select Users
                  </FormLabel>
                  <IncludeExcludeRadio
                    value={contactAttributes?.users_exclusive}
                    onChange={() => 
                      setValue(
                        `${prefix}filter_and_sort.contact_attributes.users_exclusive`, 
                        !contactAttributes?.users_exclusive
                      )
                    }
                    size="sm"
                  />
                </HStack>
                <DebouncedAsyncSelect
                  key={mappedRoles}
                  loadOptions={(searchStr) =>
                    roleChecker(role)
                      ? loadDealershipUsers(searchStr, mappedRoles, id)
                      : loadUsers(searchStr, id, mappedRoles)
                  }
                  defaultOptions
                  value={value}
                  onChange={(value: any) => {
                    setValue(
                      `${prefix}filter_and_sort.contact_attributes.users`,
                      value
                    );
                  }}
                  isMulti={true}
                  placeholder="Select Multiple Users"
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />
              </Box>
            );
          }}
          name={`${prefix}filter_and_sort.contact_attributes.users`}
          control={control}
          rules={{
            required: true,
          }}
        />
      </Box>
    </>
  );
}

export default ContactProperties;
